import { doc, collection, deleteDoc, query, getDocs, where } from 'firebase/firestore';
import { db } from 'src/config/firebase';
import api from './axiosConfig';
import  { addProyectoToEmpresa } from 'src/services/empresaService';
import movimientosService from 'src/services/movimientosService';


// ─── Cache en memoria para proyectos ─────────────────────────────────────
// TTL: 5 min. Pensado para evitar N+1 cuando varias partes de la UI piden
// el mismo proyecto en la misma pantalla (ej: lista de tickets con muchos
// proyectos). Coalesce de requests en vuelo para no disparar duplicados.
const _proyectoCache = new Map();      // id -> { data, expiresAt }
const _proyectoInflight = new Map();   // id -> Promise<proyecto>
const _empresaProyectosCache = new Map(); // empresaId -> { data, expiresAt }
const _empresaProyectosInflight = new Map(); // empresaId -> Promise<proyectos[]>
const PROYECTO_TTL_MS = 5 * 60 * 1000;

const _isFresh = (entry) => entry && entry.expiresAt > Date.now();

/**
 * Extrae el empresaId del objeto user.
 * Formatos esperados (post-migración a Mongo):
 *  - user.empresaId / user.empresa_id (string)
 *  - user.empresa (string)
 *  - user.empresa.id / user.empresa._id (objeto)
 */
const _extractEmpresaIdFromUser = (user) => {
  if (!user) return null;
  if (typeof user.empresaId === 'string' && user.empresaId) return user.empresaId;
  if (typeof user.empresa_id === 'string' && user.empresa_id) return user.empresa_id;
  const e = user.empresa;
  if (!e) return null;
  if (typeof e === 'string') return e || null;
  return e.id || e._id || null;
};

/**
 * Extrae el id de un proyecto desde una referencia simple (string o {id/_id}).
 */
const _extractProyectoIdFromRef = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref || null;
  return ref.id || ref._id || null;
};

/**
 * Invalida la caché de proyectos. Llamar tras crear/editar/eliminar.
 * @param {object} opts - { id?: string, empresaId?: string, all?: boolean }
 */
export const invalidarProyectoCache = ({ id, empresaId, all } = {}) => {
  if (all) {
    _proyectoCache.clear();
    _empresaProyectosCache.clear();
    return;
  }
  if (id) _proyectoCache.delete(id);
  // Sin empresaId no sabemos a qué lista pertenece el proyecto: se vacían todas.
  // Invalidar solo la caché individual dejaba el listado por empresa (TTL 5 min)
  // devolviendo el estado viejo, que es por qué el switch Activo/Inactivo no se
  // movía hasta recargar la página.
  if (empresaId) _empresaProyectosCache.delete(empresaId);
  else _empresaProyectosCache.clear();
};


/**
 * Obtiene los proyectos de una empresa en una sola query (endpoint batch).
 * @param {string} empresaId - ID de la empresa.
 * @returns {Promise<object[]>}
 */
export const getProyectosByEmpresaId = async (empresaId, { forceRefresh = false } = {}) => {
  if (!empresaId) return [];

  // Cache hit
  if (!forceRefresh) {
    const cached = _empresaProyectosCache.get(empresaId);
    if (_isFresh(cached)) return cached.data;
  }

  // Coalesce: si ya hay una request en vuelo para esta empresa, reusala.
  if (_empresaProyectosInflight.has(empresaId)) {
    return _empresaProyectosInflight.get(empresaId);
  }

  const promise = (async () => {
    try {
      const response = await api.get(`/proyecto/empresa/${empresaId}`);
      const proyectos = (response.data || []).filter(p => p && p.eliminado !== true);
      _empresaProyectosCache.set(empresaId, { data: proyectos, expiresAt: Date.now() + PROYECTO_TTL_MS });
      // Sembrá también la caché individual para futuras llamadas a getProyectoById.
      for (const p of proyectos) {
        const pid = p?._id || p?.id;
        if (pid) _proyectoCache.set(pid, { data: p, expiresAt: Date.now() + PROYECTO_TTL_MS });
      }
      return proyectos;
    } catch (err) {
      // 401/403: relanzar en vez de devolver []. Si devolviéramos vacío, los
      // callers (getProyectosByEmpresa / getProyectosFromUser) caen al fallback
      // N+1 (un GET por proyecto) con el mismo token vencido — una falla de auth
      // pasaría a costar 7+ requests en vez de 1. El interceptor ya intentó
      // refrescar el token antes de llegar acá.
      if ([401, 403].includes(err?.response?.status)) throw err;
      console.error('Error al obtener proyectos por empresa:', err);
      return [];
    } finally {
      _empresaProyectosInflight.delete(empresaId);
    }
  })();

  _empresaProyectosInflight.set(empresaId, promise);
  return promise;
};

/**
 * Obtiene los proyectos de un empresa a partir de las referencias almacenadas en el atributo proyectos.
 * @param {object} empresa - El objeto empresa que contiene las referencias de los proyectos.
 * @returns {Promise<object[]>} - Retorna un array con los proyectos o un array vacío si no se encuentran o hay un error.
 */
export const getProyectosByEmpresa = async (empresa) => {
    try {
      if (!empresa || !empresa.proyectosIds) {
        console.log('Referencias de proyectos no proporcionadas o incompletas en el objeto usuario');
        return [];
      }

      // Path principal: 1 sola request al endpoint batch por empresa, y filtramos
      // localmente por los ids que tiene la empresa. Evita el N+1 (1 GET por
      // proyecto) que saturaba el backend con tokens vencidos.
      const empresaId = empresa.id || empresa._id || null;
      if (empresaId) {
        const todosDeEmpresa = await getProyectosByEmpresaId(empresaId);
        if (Array.isArray(todosDeEmpresa) && todosDeEmpresa.length > 0) {
          const idsEmpresa = new Set(empresa.proyectosIds.map(String));
          const filtrados = todosDeEmpresa.filter(
            (p) => p && p.eliminado !== true && (idsEmpresa.has(String(p._id)) || idsEmpresa.has(String(p.id)))
          );
          if (filtrados.length > 0) return filtrados;
          // Si el filtro quedó vacío (ids del objeto empresa desfasados del batch),
          // caemos al path por id, que ya usa caché + coalescing.
        }
      }

      // Fallback: pedir uno por uno (pasa por caché en memoria y coalescing).
      const proyectos = await Promise.all(empresa.proyectosIds.map(async (id) => {
        const proyectoData = await getProyectoById(id);
        return proyectoData;

      }));

      return proyectos.filter(proyecto => proyecto !== null && proyecto.eliminado !== true);
    } catch (err) {
      console.error('Error al obtener los proyectos del usuario:', err);
      return [];
    }
  };

/**
 * Obtiene los proyectos de un usuario a partir de las referencias almacenadas en el atributo proyectos.
 * @param {object} user - El objeto usuario que contiene las referencias de los proyectos.
 * @returns {Promise<object[]>} - Retorna un array con los proyectos o un array vacío si no se encuentran o hay un error.
 */
export const getProyectosFromUser = async (user) => {
  try {
    if (!user || !user.proyectos) {
      console.log('Referencias de proyectos no proporcionadas o incompletas en el objeto usuario');
      return [];
    }

    // Resolver empresaId con fallbacks. Todos los usuarios deberían tener uno;
    // si no aparece en los campos directos, lo resolvemos vía API.
    let empresaId = _extractEmpresaIdFromUser(user);
    if (!empresaId) {
      try {
        // Import dinámico para evitar ciclo de dependencias con empresaService.
        const { getEmpresaDetailsFromUser } = await import('./empresaService');
        const empresa = await getEmpresaDetailsFromUser(user);
        empresaId = empresa?.id || empresa?._id || null;
      } catch (e) {
        console.warn('No se pudo resolver empresaId vía getEmpresaDetailsFromUser:', e?.message);
      }
    }

    // Construir el set de ids del usuario una sola vez.
    const idsUser = new Set();
    for (const ref of user.proyectos) {
      const pid = _extractProyectoIdFromRef(ref);
      if (pid) idsUser.add(pid);
    }

    // Path principal: batch por empresa + filtro local. 1 request total.
    if (empresaId) {
      const todosDeEmpresa = await getProyectosByEmpresaId(empresaId);
      if (Array.isArray(todosDeEmpresa) && todosDeEmpresa.length > 0) {
        const filtrados = todosDeEmpresa.filter(
          (p) => p && (idsUser.has(p._id) || idsUser.has(p.id)) && p.eliminado !== true
        );
        if (filtrados.length > 0) return filtrados;
        // Si el filtro quedó vacío puede ser que los ids del user no estén en la
        // empresa (caso raro). Caemos al path por id, que ya usa caché y coalescing.
      }
    }

    // Fallback: pedir uno por uno. Igualmente pasa por la caché en memoria y
    // por el coalescing, así que duplicados se deduplican automáticamente.
    const proyectos = await Promise.all(
      Array.from(idsUser).map((pid) => getProyectoById(pid))
    );

    return proyectos.filter((p) => p !== null && p?.eliminado !== true);
  } catch (err) {
    console.error('Error al obtener los proyectos del usuario:', err);
    return [];
  }
};

// ... (tu código existente)

/**
 * Obtiene un proyecto específico por su ID.
 * @param {string} id - El ID del proyecto a obtener.
 * @returns {Promise<object|null>} - Retorna un objeto con los datos del proyecto o null si no se encuentra.
 */
export const getProyectoById = async (id, { forceRefresh = false } = {}) => {
  if (!id) return null;

  // Cache hit
  if (!forceRefresh) {
    const cached = _proyectoCache.get(id);
    if (_isFresh(cached)) return cached.data;
  }

  // Coalesce: si ya hay una request en vuelo para este id, reusala.
  if (_proyectoInflight.has(id)) {
    return _proyectoInflight.get(id);
  }

  const promise = (async () => {
    try {
      const response = await api.get(`/proyecto/${id}`);
      const data = response.data;
      if (data) {
        _proyectoCache.set(id, { data, expiresAt: Date.now() + PROYECTO_TTL_MS });
      }
      return data;
    } catch (err) {
      console.error('Error al obtener el proyecto:', err);
      return null;
    } finally {
      _proyectoInflight.delete(id);
    }
  })();

  _proyectoInflight.set(id, promise);
  return promise;
};

// Claves que nunca viajan en un update: Mongo rechaza un $set sobre _id, y los
// totales los mantiene el backend con cada movimiento — reescribirlos con la
// copia que tiene el navegador pisaría saldos con datos viejos. Varios callers
// pasan `{ ...proyecto, campoEditado }`, así que se filtran acá y no en cada
// pantalla.
const NO_ACTUALIZABLES = [
  '_id', 'id', 'empresa_id', 'createdAt', 'updatedAt',
  'totalPesos', 'totalDolares', 'totalARS',
];

/**
 * Actualiza un proyecto existente con un patch parcial.
 * El patch viaja tal cual: el endpoint hace $set parcial, así que un campo
 * ausente queda como estaba. Reconstruir el proyecto entero acá era lo que
 * reactivaba obras inactivas y borraba el avance estimado en cada guardado.
 */
export const updateProyecto = async (id, patch, empresaId = null) => {
  try {
    const body = { ...patch };
    NO_ACTUALIZABLES.forEach((k) => delete body[k]);
    await api.put(`/proyecto/${id}`, body);
    invalidarProyectoCache({ id, empresaId });

    if (empresaId) {
      try {
        await api.post('/cache/invalidate', { tipo: 'empresa', id: empresaId });
        console.log('Caché invalidado tras actualizar proyecto');
      } catch (error) {
        console.warn('Error invalidando caché:', error);
      }
    }

    console.log('Proyecto actualizado con éxito');
    return true;
  } catch (err) {
    console.error('Error al actualizar el proyecto:', err);
    return false;
  }
};

export const actualizarPestanasOcultas = async (id, pestanasOcultas, empresaId = null) => {
  await api.patch(`/proyecto/${id}/pestanas-ocultas`, { pestanas_ocultas: pestanasOcultas });
  invalidarProyectoCache({ id, empresaId });
};

export const recargarProyecto = async (idProyecto) => {
  try {
    const response = await api.get(`recargar_sheets/${idProyecto}`);
    if (response.status === 201) {
        console.log('Sheets actualizados');
        return true;
    } else {
        console.error('Error al actualizando sheets');
        return false;
    }
  } catch (err) {
      console.error('Error al actualizando sheets:', err);
      return false;
  }
}

/**
 * Actualiza todos los sheets de los proyectos de una empresa en base a la configuración actual de la empresa.
 * @param {string} empresaId - ID de la empresa.
 * @param {string|null} proyectoId - (opcional) ID de un proyecto específico a actualizar. Si es null, actualiza todos.
 * @returns {Promise<{ success: boolean, detalles?: any }>} - Resultado de la operación.
 */
export const actualizarSheetsDesdeBaseEmpresa = async (empresaId, proyectoId = null) => {
  try {
    const response = await api.post('/proyecto/empresa/actualizar-base', {
      empresaId,
      proyectoId
    });

    if (response.status === 200) {
      try {
        await api.post('/cache/invalidate', { tipo: 'empresa', id: empresaId });
      } catch (error) {
        console.warn('Error invalidando caché:', error);
      }
      console.log('Proyectos actualizados con éxito:', response.data.detalles);
      return { success: true, detalles: response.data.detalles };
    } else {
      console.error('Error en la respuesta:', response.data);
      return { success: false };
    }
  } catch (err) {
    console.error('Error al actualizar los proyectos con la base de la empresa:', err);
    return { success: false, error: err.message };
  }
};


export const hasPermission = async (fileId) => {
  try {
    const response = await api.get(`permisosDrive/${fileId}`);
    if (response.status === 201) {
        return response.data.hasPermission;
    } else {
        console.error('Error al verificando permisos');
        return false;
    }
  } catch (err) {
      console.error('Error al verificando permisos', err);
      return false;
  }
}

/**
 * Crea un nuevo proyecto utilizando la API en lugar de Firebase directamente.
 * @param {object} proyecto - Un objeto con los datos del proyecto a crear.
 * @param {string} empresaId - El ID de la empresa a la que se asociará el proyecto.
 * @returns {Promise<{proyecto: object}>} Lanza si la creación del proyecto falla.
 */
export const crearProyecto = async (proyecto, empresaId) => {
  // Los errores se propagan: devolver null ante un fallo hacía que la pantalla
  // no pudiera distinguir el éxito del fracaso y anunciara igual "creado con éxito".
  const response = await api.post('/proyecto/', {
    ...proyecto,
    empresaId,  // Enviar la empresa ID en el body
  });

  if (response.status !== 201 || !response.data?.proyecto) {
    throw new Error('El backend no devolvió el proyecto creado');
  }

  try {
    await api.post('/cache/invalidate', { tipo: 'empresa', id: empresaId });
  } catch (error) {
    console.warn('Error invalidando caché:', error);
  }
  invalidarProyectoCache({ empresaId });
  return { proyecto: response.data.proyecto };
};

/**
 * Crea el presupuesto tradicional del alta, si el proyecto tiene m² y costo objetivo y todavía no
 * tiene obra ni presupuestos. Lanza si falla; el proyecto ya existe, así que es un aviso, no un error.
 */
export const crearPresupuestoInicial = async (proyectoId, empresaId) => {
  const { data } = await api.post(`/proyecto/${proyectoId}/presupuesto-inicial`, { empresa_id: empresaId });
  return data;
};


/**
 * Elimina un proyecto y todos sus movimientos asociados.
 * @param {string} proyectoId - El ID del proyecto a eliminar.
 * @returns {Promise<boolean>} - Retorna true si la eliminación fue exitosa, false si falló.
 */
export const softDeleteMovimientosByProyectoId = async (proyectoId) => {
  try {
    await api.delete(`movimientos/by-proyecto/${proyectoId}`);
    return true;
  } catch (err) {
    console.error('Error al soft-delete movimientos del proyecto:', err);
    return false;
  }
};

// Al borrar la obra o caja entera no hay a quién preguntar por los pagos compartidos (409):
// se desimputan, así el pago sigue activo y su plata queda como saldo a favor del proveedor.
const borrarMovimientoEnBloque = async (id) => {
  try {
    return await movimientosService.deleteMovimientoById(id);
  } catch (err) {
    if (err.status === 409 && err.pagos?.length) {
      const pagos = Object.fromEntries(err.pagos.map((p) => [p.pagoId, 'desimputar']));
      return movimientosService.deleteMovimientoById(id, { pagos }).catch((e) => {
        console.error('Error al borrar el movimiento', id, e.message);
        return false;
      });
    }
    console.error('Error al borrar el movimiento', id, err.message);
    return false;
  }
};

export const deleteProyectoById = async (proyectoId, empresaId = null) => {
  try {
    // Obtener IDs de movimientos asociados al proyecto via API
    const response = await api.get(`movimientos/ids-by-proyecto/${proyectoId}`);
    const ids = response.data?.ids || [];

    const deleteMovementsPromises = ids.map(borrarMovimientoEnBloque);
    await Promise.all(deleteMovementsPromises);

    // Elimina el proyecto via REST API
    await api.delete(`/proyecto/${proyectoId}`);
    invalidarProyectoCache({ id: proyectoId, empresaId });

    if (empresaId) {
      try {
        await api.post('/cache/invalidate', { tipo: 'empresa', id: empresaId });
      } catch (error) {
        console.warn('Error invalidando caché:', error);
      }
    }

    console.log('Proyecto y movimientos eliminados con éxito');
    return true;
  } catch (err) {
    console.error('Error al eliminar el proyecto y sus movimientos:', err);
    return false;
  }
};

export const deleteCajaById = async (cajaId) => {
  try {
    // Obtener IDs de movimientos asociados a la caja via API
    const response = await api.get(`movimientos/ids-by-caja/${cajaId}`);
    const ids = response.data?.ids || [];

    const deleteMovementsPromises = ids.map(borrarMovimientoEnBloque);
    await Promise.all(deleteMovementsPromises);

    // Elimina la caja una vez que todos los movimientos se han eliminado
    const cajaDocRef = doc(db, 'cajas', cajaId);
    await deleteDoc(cajaDocRef);

    console.log('Caja y movimientos eliminados con éxito');
    return true;
  } catch (err) {
    console.error('Error al eliminar el caja y sus movimientos:', err);
    return false;
  }
};

/**
 * Obtiene todas las cajas de una empresa filtradas por empresa_id.
 * @param {string} empresaId - El ID de la empresa para filtrar las cajas.
 * @returns {Promise<Array<object>>} - Retorna un array de cajas que pertenecen a la empresa especificada.
 */
export const getCajasByEmpresaId = async (empresaId) => {
  try {
    const cajasCollection = collection(db, 'cajas');
    const cajasQuery = query(cajasCollection, where('empresa_id', '==', empresaId));
    const querySnapshot = await getDocs(cajasQuery);

    const cajas = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log('Cajas obtenidas con éxito:', cajas);
    return cajas;
  } catch (err) {
    console.error('Error al obtener las cajas:', err);
    return [];
  }
};

/**
 * Sube un archivo CSV y carga los movimientos en un proyecto.
 * @param {string} proyectoId - ID del proyecto donde se cargarán los movimientos.
 * @param {File} archivo - Archivo CSV a subir.
 * @returns {Promise<object>} - Respuesta de la API.
 */
export const subirCSVProyecto = async (proyectoId, archivo, proyectoNombre) => {
  try {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('proyecto_nombre', proyectoNombre);

    const response = await api.post(`/proyecto/${proyectoId}/cargar-movimientos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (err) {
    console.error('Error al subir el archivo CSV:', err);
    throw err;
  }
};

export const otorgarPermisosDriveProyecto = async (proyectoId) => {
  try {
    const resp = await api.post('/proyecto/otorgarPermiso', { proyectoId });
    return resp.status === 200;
  } catch (e) {
    console.error('otorgarPermisosDriveProyecto error:', e);
    return false;
  }
};


export const getGastadoPorRubroBase = async (proyectoId, empresaId) => {
  const { data } = await api.get(`/proyecto/${proyectoId}/gastado-por-rubro-base`, { params: { empresa_id: empresaId } });
  return data;
};
